import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { CompanyOverview, Health, OverviewDashboard } from '../lib/types';
import { HEALTH_LABELS, TASK_RISK_LABELS } from '../lib/types';
import { useSession } from '../lib/session';
import { useRememberedCompany } from '../lib/rememberedCompany';
import { TaskDrawer } from '../components/TaskDrawer';
import { PlanBoundary } from '../plan/PlanBoundary';
import { RoadmapPage } from '../plan/routes/Roadmap';
import { formatDate } from '../lib/dates';
import { RiskRegister } from './RiskRegister';

const DESIGN_KEY = 'hub.overview.withoutDesign';

type Tab = 'dashboard' | 'timeline' | 'risks';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'risks', label: 'Risks' },
];

/**
 * THE COMPANY OVERVIEW — the project dashboard, the timeline and the risks, for a whole company.
 *
 * A project already has all three for itself. This is the same three over every project of
 * one company, which is the view project management and the board actually need: the
 * dashboard across all of it, ONE timeline with every project a bracket over its own tasks
 * (the roadmap view itself, fed the combined plan), and every risk in one place rather than
 * scattered over a dozen project pages. "Slide deck" on the timeline exports the roadmap deck
 * for the whole company, with a risk slide added.
 *
 * Ours only, asked by role — it is how we run the account, not what the customer reads.
 */
export function CompanyOverviewPage() {
  const { companyId: fromUrl } = useParams<{ companyId: string }>();
  const { session } = useSession();
  const navigate = useNavigate();
  const ours = (session?.companies ?? []).filter(
    (c) => session?.person.isAdmin || (c.role !== null && c.role !== 'client' && c.role !== 'client_admin'),
  );
  const { companyId: remembered, remember } = useRememberedCompany(ours);
  const companyId = fromUrl ?? remembered;

  useEffect(() => {
    if (!fromUrl && companyId) navigate(`/companies/${companyId}/overview`, { replace: true });
  }, [fromUrl, companyId, navigate]);

  const [withoutDesign, setWithoutDesign] = useState(() => {
    try {
      return localStorage.getItem(DESIGN_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [data, setData] = useState<CompanyOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<{ id: string; projectId: string } | null>(null);
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'dashboard';

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setData(await api.get<CompanyOverview>(`/companies/${companyId}/overview${withoutDesign ? '?withoutDesign=true' : ''}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the overview');
    }
  }, [companyId, withoutDesign]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleDesign(next: boolean) {
    setWithoutDesign(next);
    try {
      localStorage.setItem(DESIGN_KEY, String(next));
    } catch {
      /* private browsing */
    }
  }

  if (ours.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <h3>For our own project management</h3>
          <p>The company overview is how we run an account, so it is shown to our own team only.</p>
        </div>
      </div>
    );
  }
  if (error && !data) return <div className="callout">{error}</div>;
  if (!data) return <div className="empty">Loading…</div>;

  const design = withoutDesign ? '?withoutDesign=true' : '';
  const deckQuery = (rows: string) => `${withoutDesign ? '?withoutDesign=true&' : '?'}rows=${rows}`;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">
            <Link to={`/companies/${data.company.id}`} className="muted">
              {data.company.name}
            </Link>
          </div>
          <h1>Company overview</h1>
          <p className="subtitle">
            Every project of {data.company.name} in one place — as at {formatDate(data.today)}. Every figure comes from the
            plans as they stand now.
          </p>
        </div>
        {/* The company and which projects, side by side and the same height, on the right. */}
        <div className="row wrap" style={{ gap: 10, alignItems: 'center', justifyContent: 'flex-end' }}>
          {ours.length > 1 && (
            <select
              value={data.company.id}
              aria-label="Company"
              style={{ width: 'auto' }}
              onChange={(event) => {
                remember(event.target.value);
                navigate(`/companies/${event.target.value}/overview`);
              }}
            >
              {ours.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          )}
          {/* Two choices side by side, so it is always clear which one is showing. */}
          <div className="segmented" role="group" aria-label="Which projects">
            <button type="button" className={withoutDesign ? '' : 'is-on'} aria-pressed={!withoutDesign} onClick={() => toggleDesign(false)}>
              All projects
            </button>
            <button type="button" className={withoutDesign ? 'is-on' : ''} aria-pressed={withoutDesign} onClick={() => toggleDesign(true)}>
              Without graphic design
            </button>
          </div>
        </div>
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      {/* Three views of the same company, so management has one place for all of it. */}
      <div className="lane-tabs" role="tablist" aria-label="Company overview">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? 'lane-tab is-on' : 'lane-tab'}
            onClick={() => setParams(t.key === 'dashboard' ? {} : { tab: t.key }, { replace: true })}
          >
            {t.label}
            {t.key === 'risks' && data.totals.health.red + data.totals.health.amber > 0 ? (
              <span className="lane-count">{data.totals.health.red + data.totals.health.amber}</span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <Dashboard data={data} />}

      {/*
        The roadmap view itself, fed every project's plan: each project a bracket over its
        own tasks. Its "Slide deck" button exports the company deck for the board.
      */}
      {tab === 'timeline' && (
        <div className="ov-roadmap">
          <PlanBoundary>
            <RoadmapPage
              key={`${data.company.id}${design}`}
              source={{
                url: `/companies/${data.company.id}/overview/timeline${design}`,
                deckUrl: (rows) => `/companies/${data.company.id}/overview.pptx${deckQuery(rows)}`,
                title: 'Timeline',
                subtitle: 'Every project on one timeline, each over its own tasks. "Slide deck" exports it for the board.',
              }}
            />
          </PlanBoundary>
        </div>
      )}

      {tab === 'risks' && (
        <>
          <RiskRegister companyId={data.company.id} />
          <Risks data={data} onOpenTask={setOpen} />
        </>
      )}

      {open && <TaskDrawer itemId={open.id} projectId={open.projectId} onClose={() => setOpen(null)} onChanged={load} />}
    </>
  );
}

/* ------------------------------------------------------------------- dashboard */

const rand = (value: number) => `R ${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Bar({ percent, tone }: { percent: number; tone: 'plan' | 'work' | 'ok' | 'info' | 'bad' }) {
  return (
    <div className="dash-bar">
      <span className={`dash-bar-fill tone-${tone}`} style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </div>
  );
}

/** The dashboard a project has, over every project of the company. */
function Dashboard({ data }: { data: CompanyOverview }) {
  const d: OverviewDashboard = data.dashboard;
  const left = d.tasks.total - d.tasks.finished;
  const behind = d.time.behindBy;

  return (
    <div className="dash-grid">
      <section className="card dash-card">
        <h3>Health</h3>
        <dl className="dash-health">
          <dt>Time</dt>
          <dd className={behind > 0 ? 'is-bad' : 'is-ok'}>
            {behind > 0 ? `${behind}% behind schedule.` : behind < 0 ? `${-behind}% ahead of schedule.` : 'On schedule.'}
          </dd>
          <dt>Tasks</dt>
          <dd>
            {left} of {d.tasks.total} still to be done.
          </dd>
          <dt>Workload</dt>
          <dd className={d.tasks.overdue ? 'is-bad' : 'is-ok'}>{d.tasks.overdue ? `${d.tasks.overdue} overdue.` : 'Nothing overdue.'}</dd>
          <dt>Progress</dt>
          <dd className="is-ok">{data.totals.percentComplete}% complete.</dd>
          <dt>Projects</dt>
          <dd className={data.totals.health.red ? 'is-bad' : ''}>
            {data.totals.health.red} off track · {data.totals.health.amber} at risk · {data.totals.health.green} on track
          </dd>
        </dl>
      </section>

      <section className="card dash-card">
        <h3>Tasks</h3>
        <div className="row" style={{ gap: 22, alignItems: 'center' }}>
          <Donut total={d.tasks.total} parts={[d.tasks.notStarted, d.tasks.inProgress, d.tasks.finished]} />
          <ul className="dash-legend">
            <li>
              <span className="dot tone-plan" /> Not started <strong>{d.tasks.notStarted}</strong>
            </li>
            <li>
              <span className="dot tone-info" /> In progress <strong>{d.tasks.inProgress}</strong>
            </li>
            <li>
              <span className="dot tone-ok" /> Finished <strong>{d.tasks.finished}</strong>
            </li>
          </ul>
        </div>
      </section>

      <section className="card dash-card">
        <h3>Progress by project</h3>
        <div className="dash-list">
          {d.byProject.length === 0 && <p className="dim">No project has dates yet.</p>}
          {d.byProject.map((p) => (
            <div key={p.id} className="dash-line">
              <Link to={`/projects/${p.id}`} className="dash-line-name">
                {p.name}
              </Link>
              <span className="mono dim">{p.percent}%</span>
              <Bar percent={p.percent} tone="info" />
            </div>
          ))}
        </div>
      </section>

      <section className="card dash-card">
        <h3>Time</h3>
        <div className="dash-line">
          <span className="dash-line-name">Where the plan says we should be</span>
          <span className="mono">{d.time.planPct}%</span>
          <Bar percent={d.time.planPct} tone="plan" />
        </div>
        <div className="dash-line">
          <span className="dash-line-name">Where the work says we are</span>
          <span className="mono">{d.time.workPct}%</span>
          <Bar percent={d.time.workPct} tone={d.time.workPct < d.time.planPct ? 'bad' : 'ok'} />
        </div>
        <p className="dim dash-note">
          {d.window.from && d.window.to ? `${formatDate(d.window.from)} to ${formatDate(d.window.to)}. ` : ''}
          Measured against the plans as they stand now.
        </p>
      </section>

      <section className="card dash-card">
        <h3>Cost</h3>
        {/* Measured against the budgets where any are set; against the plan otherwise. */}
        {(() => {
          const scale = d.budget?.total ?? d.cost.planned ?? 0;
          const over = (v: number | null) => d.budget !== null && v !== null && v > d.budget.total;
          return (
            <>
              <div className="dash-line">
                <span className="dash-line-name">Spent</span>
                <span className="mono" style={over(d.cost.spent) ? { color: 'var(--dash-bad)' } : undefined}>
                  {d.cost.spent === null ? 'Not known' : rand(d.cost.spent)}
                </span>
                <Bar percent={d.cost.spent && scale ? (d.cost.spent / scale) * 100 : 0} tone={over(d.cost.spent) ? 'bad' : 'info'} />
              </div>
              <div className="dash-line">
                <span className="dash-line-name">Planned</span>
                <span className="mono" style={over(d.cost.planned) ? { color: 'var(--dash-bad)' } : undefined}>
                  {d.cost.planned === null ? 'Not known' : rand(d.cost.planned)}
                </span>
                <Bar percent={d.cost.planned && scale ? (d.cost.planned / scale) * 100 : 0} tone={over(d.cost.planned) ? 'bad' : 'plan'} />
              </div>
              <div className="dash-line">
                <span className="dash-line-name">Budget</span>
                <span className="mono">{d.budget ? rand(d.budget.total) : 'None set'}</span>
                <Bar percent={d.budget ? 100 : 0} tone="ok" />
              </div>
              <p className="dim dash-note">
                {d.budget
                  ? `Budgets on ${d.budget.projects} of ${data.projects.length} project${data.projects.length === 1 ? '' : 's'}. `
                  : 'No project has a budget yet — set one in Edit project. '}
                Spent and planned come from the hours booked and estimated, at the company's rates.
              </p>
            </>
          );
        })()}
      </section>

      <section className="card dash-card">
        <h3>Workload</h3>
        <div className="dash-list">
          {d.workload.length === 0 && <p className="dim">Nothing left to do.</p>}
          {d.workload.slice(0, 8).map((p) => (
            <div key={p.name} className="dash-line">
              <span className="dash-line-name">{p.name}</span>
              <span className="dim" style={{ fontSize: 12 }}>
                {p.left} left{p.hours ? ` · ${Math.round(p.hours)}h` : ''}
                {p.overdue ? <strong style={{ color: 'var(--accent)' }}> · {p.overdue} overdue</strong> : null}
              </span>
              <div className="dash-bar">
                <span className="dash-bar-fill tone-bad" style={{ width: `${(p.overdue / p.left) * 100}%` }} />
                <span className="dash-bar-fill tone-info" style={{ width: `${100 - (p.overdue / p.left) * 100}%` }} />
              </div>
            </div>
          ))}
          {d.workload.length > 8 && <p className="dim dash-note">And {d.workload.length - 8} more people.</p>}
        </div>
      </section>

    </div>
  );
}

/** A ring split into not started, in progress and finished, with the total in the middle. */
function Donut({ total, parts }: { total: number; parts: number[] }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const tones = ['var(--dash-plan)', 'var(--dash-info)', 'var(--dash-ok)'];
  return (
    <svg width="96" height="96" viewBox="0 0 96 96" role="img" aria-label={`${total} tasks`}>
      <circle cx="48" cy="48" r={r} fill="none" stroke="var(--line)" strokeWidth="12" />
      {total > 0 &&
        parts.map((part, i) => {
          const length = (part / total) * c;
          const circle = (
            <circle
              key={i}
              cx="48"
              cy="48"
              r={r}
              fill="none"
              stroke={tones[i]}
              strokeWidth="12"
              strokeDasharray={`${length} ${c - length}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 48 48)"
            />
          );
          offset += length;
          return circle;
        })}
      <text x="48" y="54" textAnchor="middle" fontSize="20" fontWeight="700" fill="var(--text)">
        {total}
      </text>
    </svg>
  );
}

/* ----------------------------------------------------------------------- risks */

function HealthPill({ health }: { health: Health }) {
  return <span className={`health-pill health-${health}`}>{HEALTH_LABELS[health]}</span>;
}

/** Every risk of every project in one place: how each is doing and why, then what needs a decision. */
function Risks({ data, onOpenTask }: { data: CompanyOverview; onOpenTask: (t: { id: string; projectId: string }) => void }) {
  const name = new Map(data.projects.map((p) => [p.id, p.name]));
  return (
    <div className="card mt-2">
      <div className="card-head">
        <h2>Projects at risk</h2>
        <span className="dim" style={{ fontSize: 12 }}>
          Every project, worst first — and what needs a decision
        </span>
      </div>
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              <th>Project</th>
              <th>Health</th>
              <th className="right">Complete</th>
              <th className="right">Open</th>
              <th className="right">Late</th>
              <th>Target</th>
              {data.projects.some((p) => p.money) && <th>Budget</th>}
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {data.projects.map((p) => (
              <tr key={p.id}>
                <td>
                  <Link to={`/projects/${p.id}`}>
                    <strong>{p.name}</strong>
                  </Link>
                </td>
                <td>
                  <HealthPill health={p.health} />
                </td>
                <td className="right mono">{p.percentComplete}%</td>
                <td className="right mono">{p.counts.open}</td>
                <td className="right mono" style={p.counts.overdue ? { color: 'var(--accent)', fontWeight: 700 } : undefined}>
                  {p.counts.overdue || '—'}
                </td>
                <td className="mono">{p.targetDate ? formatDate(p.targetDate) : '—'}</td>
                {data.projects.some((x) => x.money) && (
                  <td className="mono" style={{ fontSize: 12 }}>
                    {p.money?.budget == null ? (
                      <span className="dim">—</span>
                    ) : (
                      <span style={p.money.spent !== null && p.money.spent > p.money.budget ? { color: 'var(--dash-bad)', fontWeight: 700 } : undefined}>
                        {p.money.spent === null ? '?' : rand(p.money.spent)} of {rand(p.money.budget)}
                      </span>
                    )}
                  </td>
                )}
                <td className="muted" style={{ fontSize: 12 }}>
                  {p.reasons.join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card-head" style={{ borderTop: '1px solid var(--line)' }}>
        <h2>Needs a decision</h2>
        <span className="dim" style={{ fontSize: 12 }}>
          Overdue, blocked, or due within a week and not yet in development
        </span>
      </div>
      {data.attention.length === 0 ? (
        <div className="empty">
          <p style={{ margin: 0 }}>Nothing is overdue, blocked or about to slip.</p>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="data">
            <tbody>
              {data.attention.map((t) => (
                <tr key={t.id} className="clickable" onClick={() => onOpenTask({ id: t.id, projectId: t.projectId })}>
                  <td style={{ width: 170 }}>
                    <span className={t.risk === 'due_soon' ? 'badge badge-warn' : 'badge badge-accent'}>
                      {t.risk ? TASK_RISK_LABELS[t.risk] : 'Blocked'}
                    </span>
                  </td>
                  <td>
                    <span className="key">{t.key}</span> {t.title}
                    <div className="dim" style={{ fontSize: 11 }}>
                      {name.get(t.projectId)}
                    </div>
                  </td>
                  <td className="muted">{t.assignees || <span style={{ color: 'var(--accent)' }}>Nobody</span>}</td>
                  <td className="mono" style={{ width: 70 }}>
                    {t.percent === null ? '—' : `${t.percent}%`}
                  </td>
                  <td className="mono" style={{ width: 100 }}>
                    {t.dueDate ? formatDate(t.dueDate) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
